import _ from 'lodash';
import PropTypes from 'prop-types';
import React, {ReactNode, useEffect, useRef, useState} from 'react';
import {faSearch} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import postal from 'postal';
import {usePopper} from 'react-popper';

import Table from '../../ui/tables/Table';
import useTableNavigation from '../../ui/tables/useTableNavigation';

import styled from 'styled-components';

import {isCharacterKeyPress} from '../../util';

import useTableStorage from '../../hooks/useTableStorage';
import PrimaryButton from '../PrimaryButton';
import SecondaryButton from '../SecondaryButton';

import {TableColumn} from '../../tableSettings';
import {focusNextTabable} from '../../util/inputUtils';
import useGlobalOkDialog from '../../hooks/useGlobalOkDialog';

const LoopupPopup = styled.div`
  background-color: white;
  box-shadow: rgba(0, 0, 0, 0.19) 0px 10px 20px, rgba(0, 0, 0, 0.23) 0px 6px 6px;
  z-index: 999999999 !important;

  .lookup-footer {
    padding-left: 5px;
    padding-right: 5px;
    padding-bottom: 5px;
  }

  .additional-search {
    float: left;
    width: 200px;
  }

  .commands-buttons {
    float: left;
    width: calc(100% - 200px);
  }

  .commands-buttons-left {
    float: left;
  }

  .commands-buttons-right {
    float: right;
  }
`;

const LookupContainer = styled.div`
  position: relative;
  width: 100%;

  .ifms-lookup-icon {
    position: absolute;
    right: 5px;
    font-size: 10px;
    top: 4px;
    color: #a1a1a1;
  }
`;

const TypeaheadTimeout = 1800;

// function focusNextTabable(anchorElement:HTMLInputElement) {
//   const tabableElements = document.getElementsByClassName('ifms-tabable');
//   const validElements = [];

//   if (!tabableElements || tabableElements.length <= 0) {
//     return;
//   }

//   for (let i = 0; i < tabableElements.length; ++i) {
//     const el = tabableElements[i] as HTMLElement;
//     if (!isElementFocusable(el)) {
//       continue;
//     }

//     for (let j = 0; j <= validElements.length; ++j) {
//       if (j === validElements.length) {
//         validElements.push(el);
//         break;
//       }
//       if (validElements[j].tabIndex <= el.tabIndex) {
//         continue;
//       }
//       validElements.splice(j, 0, el);
//       break;
//     }
//   }

//   if (!validElements || validElements.length <= 0) {
//     return;
//   }

//   let anchorFound = false;
//   for (let i = 0; i < validElements.length; ++i) {
//     if (anchorFound) {
//       validElements[i].focus();
//       return;
//     }

//     if (validElements[i] === anchorElement) {
//       anchorFound = true;
//     }
//   }
// }

export enum LookupInputType {
  AllowInputValue,
  RollBackPrevValue,
  RollBackPrevValueAllowEmpty,
  ClearBlankValue,
}

interface LastQuery {
  text: string | null,
  more: string | null,
};

function LookupField({
  query,
  placeholder = '',
  width = undefined,
  columns,
  onSelect,
  onCancel,
  labelProp,
  initialValue,
  onTextChange,
  filter,
  disabled = false,
  lookupId = undefined,
  onTabNext = undefined,
  onTabPrevious = undefined,
  searchMatchOnTab = false,
  hasLookupValue = false,
  activator = false,
  onFocus = () => {},
  onBlur = () => {},
  triggerLookupOnTab = false,
  focusableWhenDisabled = false,
  validationFor = undefined,
  backgroundColor = undefined,
  maxLength = undefined,
  initialData = undefined,
  showEmptyTable = true,
  customContent,
  isMultiple = false,
  noBorder = false,
  tableHeight = undefined,
  focusElementAfterSelect = () => null,
  userInputType = 0,
  name=undefined,
  noRightBorder = false,
  noLeftBorder = false,
  notFocusNextEleWhenSelectItem = false,
}: {
  query: (x: string | null) => Promise<Array<any>>,
  onSelect: (x: any) => any,
  onCancel: VoidFunction,
  labelProp: string,
  initialValue: string | null | undefined,
  placeholder: string,
  width: string | undefined,
  columns: Array<TableColumn>,
  disabled?: boolean,
  onTextChange: (text: string) => void,
  filter: (data: Array<any>, filterText: string) => number,
  lookupId?: string | undefined,
  onTabNext?: (() => boolean) | undefined,
  onTabPrevious?: (() => boolean) | undefined,
  hasLookupValue?: boolean,
  searchMatchOnTab: any | undefined,
  activator: any | undefined,
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void,
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void,
  triggerLookupOnTab?: boolean,
  focusableWhenDisabled?: boolean,
  validationFor?: string | undefined,
  backgroundColor?: string | undefined,
  maxLength?: number | undefined,
  initialData?: any[] | undefined,
  showEmptyTable?: boolean,
  isMultiple?: boolean,
  noBorder?: boolean,
  customContent?: ReactNode,
  tableHeight?: string|undefined,
  focusElementAfterSelect?: (x: any) => HTMLInputElement | HTMLElement | null,
  userInputType?: LookupInputType,
  name?: string | undefined,
  noRightBorder?: boolean,
  noLeftBorder?: boolean,
  notFocusNextEleWhenSelectItem?: boolean,
}) {
  const [showGlobalOkDialog] = useGlobalOkDialog();
  const channel = postal.channel('ifms');
  const tableStorage = useTableStorage();
  const autocompleteOffGuard = _.uniqueId('autocomplete-off-');

  const [initialized, setInitialized] = useState(false);
  const [customTableId] = useState(lookupId ? `${lookupId}-lookuptable` : _.uniqueId('ifms-lookuptable-'));
  const [inputId] = useState(!!lookupId ? lookupId : _.uniqueId('ifms-lookup-'));

  const timeout = null;
  const [typeQuery, setTypeQuery] = useState('');
  const [oldSelectedValue, setOldSelectedValue] = useState<string>(initialValue ?? '');

  const [lastQuery, setLastQuery] = useState<LastQuery>({text: null, more: null});

  const [data, _setData] = useState([]);
  const [selectedIndex, _setSelectedIndex] = useState(-1);

  const [showPopup, _setShowPopup] = useState(false);
  const [referenceElement, _setReferenceElement] = useState<HTMLInputElement | null>(null);
  const [popperElement, _setPopperElement] = useState<HTMLDivElement | null>(null);
  const [arrowElement, setArrowElement] = useState<HTMLDivElement | null>(null);
  const {styles, attributes} = usePopper(referenceElement, popperElement, {
    placement: 'bottom-start',
    modifiers: [{name: 'arrow', options: {element: arrowElement}}],
    strategy: 'fixed'
  });

  const {
    selectTableCol,
    selectTableRow,
    tableNavigationCleanup
  } = useTableNavigation(customTableId, setSelectedIndex, handleSelect, [], true, true, false, true, undefined, initialized);

  const additionalSearchInput = useRef<HTMLInputElement>(null);

  const popperRef = useRef<HTMLDivElement | null>(null);
  const referenceRef = useRef<HTMLInputElement | null>(null);
  const selectedIndexRef = useRef(-1);
  const dataRef = useRef([]);

  const fieldClickRef = useRef(false);
  let openLookupSubscription: ISubscriptionDefinition<any> | null = null;

  function subscribeChannels() {
    openLookupSubscription = postal.subscribe({
      channel: 'ifms',
      topic: 'ifms.lookup-open',
      callback: (data) => onEnter(data.targetId)
    });
  }

  function unsubscribeChannels() {
    if (openLookupSubscription) {
      postal.unsubscribe(openLookupSubscription);
    }
  }

  function setData(d: any) {
    dataRef.current = d;
    _setData(d);
  }

  function setSelectedIndex(i: number) {
    if (i !== selectedIndexRef.current) {
      selectedIndexRef.current = i;
      _setSelectedIndex(i);
    }
  }

  function setShowPopup(s: boolean) {
    if (!s) {
      tableNavigationCleanup();
    }
    _setShowPopup(s);
  }

  function setReferenceElement(e: HTMLInputElement | null) {
    referenceRef.current = e;
    _setReferenceElement(e);
  }

  function setPopperElement(e: HTMLDivElement | null) {
    popperRef.current = e;
    _setPopperElement(e);
  }

  useEffect(() => {
    subscribeChannels();
    document.addEventListener('click', handleClickOutside, true);

    return () => {
      unsubscribeChannels();
      document.removeEventListener('click', handleClickOutside, true);
      tableNavigationCleanup();
    };
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    if (initialData) {
      setData(initialData);
      setSelectedIndex(0);
      setLastQuery((lastQuery) => ({...lastQuery, text: ''}));
      setShowPopup(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData])

  useEffect(() => {
    selectTableRow(showPopup ? 0 : -1);
    selectTableCol(showPopup ? 0 : -1);
    setInitialized(showPopup);
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [showPopup]
  );

  useEffect(() => {
      if (referenceElement && !activator) {
        referenceElement.value = initialValue ?? '';
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [initialValue]
  );

  function processTabNext(e: React.KeyboardEvent<HTMLInputElement>) {
    if(additionalSearchInput.current){
      additionalSearchInput.current?.focus();
      e.preventDefault();
      return;
    }

    if (onTabNext) {
      const tabProcessed = onTabNext();
      if (tabProcessed) {
        e.preventDefault();
        return;
      }
    }
  }

  function processTabPrevious(e: React.KeyboardEvent<HTMLInputElement>) {
    if (onTabPrevious) {
      const tabProcessed = onTabPrevious();
      if (tabProcessed) {
        e.preventDefault();
        return;
      }
    }
  }

  function typeahead(char: string) {
    if (!showPopup) {
      return;
    }

    const newTypeQuery = `${typeQuery}${char}`;

    const existingIndex = filter(data, newTypeQuery);
    if (existingIndex >= 0) {
      setSelectedIndex(existingIndex);
      selectTableRow(existingIndex);
    }

    setTypeQuery(newTypeQuery);

    if (!!timeout) {
      clearTimeout(timeout);
    }
    setTimeout(() => setTypeQuery(''), TypeaheadTimeout);
  }

  function handleClickOutside(e: MouseEvent) {
    const target = e.target as Element;
    if (!popperRef || !referenceRef || !popperRef.current || !referenceRef.current || target?.id === inputId) {
      if (showPopup) {
        setShowPopup(false);
      }
      return;
    }

    if (!popperRef.current.contains(target) && !referenceRef.current.contains(target)) {
      handleCancel();
      e.stopPropagation();
    }
  }

  async function onActivateExternal() {
    await executeQuery();
  }

  async function onEnterWithActivator() {
    if (showPopup) {
      handleSelect(selectedIndex);
    } else {
      await executeQuery();
    }
  }

  async function onEnter(targetId?: string) {
    if (!!activator) {
      await onEnterWithActivator();
      return;
    }

    if (!referenceRef.current || (targetId && referenceRef.current.id !== targetId)) {
      return;
    }

    if (showPopup && referenceRef.current.value === lastQuery.text) {
      handleSelect(selectedIndex);
    } else {
      await executeQuery(referenceRef.current.value);
    }
  }


  async function executeQuery(text: string | null = null) {
    const queryResult = await query(text);
    if (!showEmptyTable && (!queryResult || !queryResult.length)) {
      return;
    }
    setData(queryResult);
    if (queryResult.length > 0) {
      setSelectedIndex(0);
    }

    setLastQuery({...lastQuery, text});
    setShowPopup(true);
  }

  function handleEscape(e: React.KeyboardEvent<HTMLInputElement>) {
    if (showPopup) {
      handleCancel();
      e.stopPropagation();
    }
  }

  async function doSearchMatchOnTab() {
    if (!referenceRef.current) {
      return;
    }

    const searchValue = referenceRef.current.value;
    if (!searchValue || searchValue === lastQuery.text) {
      return false;
    }

    const queryResult = await query(searchValue);
    if (!queryResult || queryResult.length <= 0) {
      return false;
    }

    const exactMatch = queryResult.find(x => x[labelProp] && x[labelProp].toLowerCase() === searchValue);
    if (exactMatch) {
      setLastQuery({...lastQuery, text: exactMatch[labelProp]});
      onSelect(exactMatch);
      return false;
    } else {
      setData(queryResult);
      if (queryResult.length > 0) {
        setSelectedIndex(0);
      }

      setLastQuery({...lastQuery, text: searchValue});
      setShowPopup(true);

      if (referenceRef.current) {
        referenceRef.current.focus();
      }

      return true;
    }
  }

  // function handleClickInsideText() {
  //   if (showPopup) {
  //     handleCancel();
  //   }
  // }

  function isEditTableCell(element: Element): boolean {
    const divElements = document.querySelectorAll('div[id$="-editabletable"]');
    return Array.from(divElements).some(div => div.contains(element));
  }

  async function executeLookupOnTab() {
    if (referenceRef.current && referenceRef.current.value) {
      // Let's try to query, if there is only single result with exact same value, select it. Otherwise, show lookup popup
      const queryResult = await query(referenceRef.current.value);

      if (queryResult && queryResult.length === 1 && queryResult[0][labelProp] && queryResult[0][labelProp].toLowerCase() === referenceRef.current.value) {
        onSelect(queryResult[0]);

        // Find next tabbable and focus it, since default event propagation needed to be stopped
        setTimeout(() => {
          if (referenceRef.current)
            focusNextTabable(referenceRef.current);
        }, 10)
        return;
      }
    }
    onEnter();
  }

  async function processSpecialKeys(e: React.KeyboardEvent<HTMLInputElement>) {    
    if (!e || !e.keyCode) {
      return;
    }

    // focusable when disabled fields are actually enabled text fields, change is not allowed
    if (disabled) {
      if (!(e.ctrlKey && (e.keyCode === 67 || e.keyCode === 65))) {
        e.stopPropagation();
        e.preventDefault();
        return;
      }
    }

    const tab = 9;

    if (e.keyCode === tab) {
      if (triggerLookupOnTab && !!referenceRef.current && !!referenceRef.current.value && !hasLookupValue) {
        e.stopPropagation();
        e.preventDefault();
        executeLookupOnTab();
        return;
      }
      if (searchMatchOnTab && !hasLookupValue) {
        const stopFurther = await doSearchMatchOnTab();
        if (stopFurther) {
          e.stopPropagation();
          e.preventDefault(); // Typing is not allowed
          return;
        }
      }

      if (e.shiftKey) {
        processTabPrevious(e);
      } else {
        processTabNext(e);
      }
      return;
    }

    switch (e.keyCode) {
      case 40: // Arrow down
        if (showPopup && (selectedIndex < data.length - 1)) {
          setSelectedIndex(selectedIndex + 1);
          selectTableRow(selectedIndex + 1);

        } else {
          // const parent = e?.target as HTMLElement | undefined;
          // const parentId = parent?.id;
          // if (!!parentId) {
          //   channel.publish('ifms.control-navigation', {
          //     parentId,
          //     keyCode: e.keyCode
          //   });
          // }
          // e.preventDefault();
          // e.stopPropagation();
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        return;
      case 27:
        handleEscape(e);
        return;
      case 38: // Arrow up
        if (showPopup && (selectedIndex > 0)) {
          setSelectedIndex(selectedIndex - 1);
          selectTableRow(selectedIndex - 1);
        } else {
          // const parent = e?.target as HTMLElement | undefined;
          // const parentId = parent?.parentElement?.id;
          // if (!!parentId) {
          //   channel.publish('ifms.control-navigation', {
          //     parentId,
          //     keyCode: e.keyCode
          //   });
          // }
          // e.preventDefault();
          // e.stopPropagation();
          return;
        }
        return;
      case 9: // Tab
        handleCancel();
        e.preventDefault();
        e.stopPropagation();
        return;
      case 13: // Enter
        onEnter();
        e.preventDefault();
        e.stopPropagation();
        return;
      default:
    }

    if (showPopup) {
      e.stopPropagation();
      //e.preventDefault(); // Typing is not allowed

      if (isCharacterKeyPress(e)) {
        typeahead(e.key);
        return;
      }
    }
  }

  async function handleSelect(index: number) {
    const customTable = document.getElementById(customTableId);
    const isMultipleHeader = customTable?.querySelectorAll("[data-column-id='select']");
    if (index < 0 || index > dataRef.current.length - 1) {
      handleCancel();
    } else {
      if (isMultipleHeader && isMultipleHeader.length === 0){
        setShowPopup(false);
        const referenceElementValue = dataRef.current[index][labelProp] ? dataRef.current[index][labelProp] : '';
        if (referenceRef.current) {
          referenceRef.current.value = referenceElementValue;
          onTextChange(referenceElementValue);
          setOldSelectedValue(referenceElementValue);
        }
        // 선택후에는 값을 지워주도록 수정. 다음번에 영향을 줌.
        //setLastQuery({...lastQuery, text: referenceElementValue});
        setLastQuery({...lastQuery, text: '', more: ''});
        const result = onSelect(dataRef.current[index]);
        handleCancel();

        const focusElement = focusElementAfterSelect(result);
        if (focusElement) {
          if (focusElement instanceof HTMLInputElement) {
            if(result) await showGlobalOkDialog(result);
            focusElement?.select();
          }

          else if (focusElement instanceof HTMLElement) {
            if(result) await showGlobalOkDialog(result);
            focusElement?.focus();
          }
          else return;
        }
        else {
          //focus next tabable field after select
          setTimeout(() => {
            if (isEditTableCell(referenceRef.current as HTMLInputElement)) {
              channel.publish('ifms.editable-table-onEnter', { backFocus: false, changeFocus: true, element: referenceRef.current });
            }
            else {
              if (!notFocusNextEleWhenSelectItem) {
                if (referenceRef.current)
                  focusNextTabable(referenceRef.current)
              }
            }
          }, 10)
        }
      }
    }
  }

  function handleOk(selectedIndex: number) {
    if (!isMultiple) {
      handleSelect(selectedIndex)
    } else {
      setShowPopup(false);
    }
  }

  function handleCancel() {
    setShowPopup(false);
    if (additionalSearchInput && additionalSearchInput.current) {
      additionalSearchInput.current.value = '';
    }
    onCancel();
    if (referenceRef.current && !activator) {
      referenceRef.current.focus();
      //referenceRef.current.select();
    }
  }

  function handleFilterMore() {
    if (additionalSearchInput && additionalSearchInput.current) {
      const existingIndex = filter(data, additionalSearchInput.current.value);
      if (existingIndex >= 0) {
        setSelectedIndex(existingIndex);
        selectTableRow(existingIndex);
      }
      setLastQuery({...lastQuery, more: additionalSearchInput.current.value});
    }
  }

  function processFilterMoreSpecialKeys(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!e || !e.keyCode) {
      return;
    }

    switch (e.keyCode) {
      case 40: // Arrow down
        if (selectedIndex < data.length - 1) {
          setSelectedIndex(selectedIndex + 1);
          selectTableRow(selectedIndex + 1);
        }
        return;
      case 27:
        handleEscape(e);
        return;
      case 38: // Arrow up
        if (selectedIndex > 0) {
          setSelectedIndex(selectedIndex - 1);
          selectTableRow(selectedIndex - 1);
        }
        return;
      case 9: // Tab
        handleCancel();
        e.preventDefault();
        e.stopPropagation();
        return;
      case 13: // Enter
        if (additionalSearchInput && additionalSearchInput.current && additionalSearchInput.current.value !== lastQuery.more) {
          //handleFilterMore();
          executeQuery(additionalSearchInput.current.value);
        } else {
          handleSelect(selectedIndex);
        }
        e.stopPropagation();  // Enter Key Event가 이후까지 영향을 주지 않도록 함.
        return;
      default:
    }
  }

  function onFindMoreClick() {
    handleFilterMore();
    if (additionalSearchInput && additionalSearchInput.current) {
      additionalSearchInput.current.focus();
    }
  }

  function handleFocus(e: React.FocusEvent<HTMLInputElement>) {
    // Grid > Lookup을 동작할 때 창 내부 항목을 클릭하면 Lookup창이 닫히는 문제
    // if (showPopup) {
    //   setShowPopup(false);
    // }

    if (onFocus) {
      onFocus(e);
    }
  }

  function onClick(e:React.MouseEvent) {
    if (!disabled && !fieldClickRef.current) {
      (e.target as HTMLInputElement).select();
      fieldClickRef.current = true;
    }
  }

  function handleBlur(e: React.FocusEvent<HTMLInputElement>) {
    if (popperRef.current) {
      return;
    }

    if (_.isEmpty(e.target.value))
      setOldSelectedValue('');

    switch (userInputType) {
      case LookupInputType.RollBackPrevValue:
        e.target.value = oldSelectedValue;
        break;
      case LookupInputType.RollBackPrevValueAllowEmpty:
        if (!_.isEmpty(e.target.value))
          e.target.value = oldSelectedValue;
        break;
      case LookupInputType.ClearBlankValue:
        e.target.value = '';
        break;
    }

    if (onBlur) {
      onBlur(e);
    }
    
    fieldClickRef.current = false;
  }

  let appliedPopperStyles = {
    ...styles.popper,
  };

  // if(isMultiple) {
  //   appliedPopperStyles.height = 250;
  // }

  if (!!width) {
    appliedPopperStyles = {...appliedPopperStyles, width};
  }

  const tableProps = {
    customTableId,
    overflowX: 'hidden',
    overflowY: 'scroll',
    height: tableHeight ?? '250px',
    
  
    columns,
    data,
    selectable: true,
    alternateRows: true,
    headersColor: 'white',
    // onRowSelected: setSelectedIndex,
    // onRowDoubleClick: handleSelect,
    selectedRowIndex: selectedIndex,
    customStorage: tableStorage,
    useHighlightCellOnMouseover: false
  };

  let classes = focusableWhenDisabled && disabled
    ? 'ifms-lookup e-ifms-input e-outline ifms-tabable e-ifms-focusable-when-disabled'
    : 'ifms-lookup e-ifms-input e-outline ifms-tabable';

  if (!disabled) {
    classes = `${classes} ifms-tabable-active`;
  }
  else {
    classes = `${classes} selenium-test-disabled`;
  }

  let inputStyle:React.CSSProperties = {paddingRight: '20px', backgroundColor};
  if (noBorder) {
    inputStyle.border = 'none';
  }
  if (noRightBorder) {
    inputStyle.borderRight = 'none';
  }
  if (noLeftBorder) {
    inputStyle.borderLeft = 'none';
  }

  return (
    <>
      {!!activator &&
        <>{activator(onActivateExternal, setReferenceElement, processSpecialKeys)}</>
      }
      {!activator &&
        <LookupContainer>
          {!!customContent ? (
            <div ref={setReferenceElement} onClick={() => { onEnter(); }}>{customContent}</div>
          ) : (
            <>
              <input
                id={inputId}
                name={name}
                disabled={disabled && !focusableWhenDisabled}
                ref={setReferenceElement}
                type="text"
                className={classes}
                placeholder={placeholder}
                onKeyDown={processSpecialKeys}
                defaultValue={initialValue || ''}
                onChange={e => onTextChange(e.target.value)}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onClick={onClick}
                style={inputStyle}
                //autoComplete={autocompleteOffGuard}
                autoComplete="off"
                list={autocompleteOffGuard}
                data-tooltip="-from-value"
                
                data-tooltip-onlywhencutoff
                data-validationfor={validationFor}
                maxLength={maxLength}
              />
              <FontAwesomeIcon color='black' icon={faSearch} className="ifms-lookup-icon"/>
            </>
          )}
        </LookupContainer>
      }
      {showPopup &&
        <LoopupPopup ref={setPopperElement} style={appliedPopperStyles} {...attributes.popper}>
          <Table {...tableProps} />
          <div ref={setArrowElement} style={styles.arrow}/>
          {//!isMultiple && 
           (
            <div className="lookup-footer">
              <div className="additional-search">
                <input
                  ref={additionalSearchInput}
                  id='ifms-lookup-search'
                  type="text"
                  className="e-ifms-input e-outline ifms-tabable"
                  placeholder="Filter search list"
                  onKeyDown={processFilterMoreSpecialKeys}
                  autoComplete="off"
                  list="autocompleteOff"
                  data-tooltip="-from-value"
                  
                  data-tooltip-onlywhencutoff
                />
              </div>
              <div className="commands-buttons">
                <div className="commands-buttons-left">
                  &nbsp;<PrimaryButton id="ifms-lookup-find" skipTableCheck size="small" onClick={onFindMoreClick}>Find</PrimaryButton>
                </div>
                <div className="commands-buttons-right">
                  <PrimaryButton id="ifms-lookup-ok" skipTableCheck size="small" onClick={() => handleOk(selectedIndex)}>Ok</PrimaryButton>&nbsp;
                  <SecondaryButton id="ifms-lookup-cancel" skipTableCheck size="small" onClick={handleCancel}>Cancel</SecondaryButton>
                </div>
              </div>
              <div className="clearfix"></div>
            </div>
          )}
        </LoopupPopup>
      }
    </>
  );

}

LookupField.defaultProps = {
  placeholder: '',
  onSelect: () => {},
  onTextChange: () => {},
  filter: () => -1,
  disabled: false,
  searchMatchOnTab: false,
  triggerLookupOnTab: false,
  focusableWhenDisabled: false,
  showEmptyTable: true,
  isMultiple: false,
  noBorder: false,
  noRightBorder: false,
  noLeftBorder: false,
};

LookupField.propTypes = {
  placeholder: PropTypes.string.isRequired,
  query: PropTypes.func.isRequired,
  width: PropTypes.string,
  columns: PropTypes.array.isRequired,
  onSelect: PropTypes.func.isRequired,
  onTextChange: PropTypes.func.isRequired,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onCancel: PropTypes.func.isRequired,
  labelProp: PropTypes.string.isRequired,
  initialValue: PropTypes.string,
  filter: PropTypes.func.isRequired,
  disabled: PropTypes.bool.isRequired,
  lookupId: PropTypes.string,
  onTabNext: PropTypes.func,
  onTabPrevious: PropTypes.func,
  searchMatchOnTab: PropTypes.bool.isRequired,
  activator: PropTypes.func,
  hasLookupValue: PropTypes.bool,
  triggerLookupOnTab: PropTypes.bool.isRequired,
  focusableWhenDisabled: PropTypes.bool.isRequired,
  backgroundColor: PropTypes.string,
  maxLength: PropTypes.number,
  initialData: PropTypes.array,
  showEmptyTable: PropTypes.bool,
  isMultiple: PropTypes.bool,
  noBorder: PropTypes.bool,
  tableHeight: PropTypes.string,
  noRightBorder: PropTypes.bool,
  noLeftBorder: PropTypes.bool,
};

export default React.memo(LookupField);
